import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { 
  Save,
  Check,
  X,
  BookOpen,
  Globe
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { code: 'ro', name: 'Română', flag: '🇷🇴' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
];

const AdminMyStory = () => {
  const [selectedLang, setSelectedLang] = useState('ro');
  const [storyData, setStoryData] = useState({
    ro: { title: '', intro: '', sections: [] },
    en: { title: '', intro: '', sections: [] },
    de: { title: '', intro: '', sections: [] }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const fetchStory = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin/mystory`);
        if (response.data && Object.keys(response.data).length > 0) {
          setStoryData(prev => ({ ...prev, ...response.data }));
        }
      } catch (error) {
        console.error('Error fetching story:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStory();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      await axios.put(`${API_URL}/api/admin/mystory`, storyData);
      setMessage({ type: 'success', text: 'Povestea a fost salvată!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Eroare la salvarea poveștii.' });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setStoryData(prev => ({
      ...prev,
      [selectedLang]: {
        ...prev[selectedLang],
        [field]: value
      }
    }));
  };

  const currentLangData = storyData[selectedLang] || { title: '', intro: '', sections: [] };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Povestea Mea</h1>
        <p className="text-slate-400 mt-1">Editează conținutul paginii "Povestea Mea" pentru fiecare limbă</p>
      </div>

      {/* Language Selector */}
      <div className="flex flex-wrap gap-2">
        {LANGUAGES.map(lang => (
          <button
            key={lang.code}
            onClick={() => setSelectedLang(lang.code)}
            className={`px-4 py-2 rounded-xl flex items-center gap-2 transition-colors ${
              selectedLang === lang.code
                ? 'bg-sky-500 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700'
            }`}
          >
            <span>{lang.flag}</span>
            <span>{lang.name}</span>
          </button>
        ))}
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-xl flex items-center gap-2 ${
          message.type === 'success' 
            ? 'bg-green-500/20 text-green-400 border border-green-500/50' 
            : 'bg-red-500/20 text-red-400 border border-red-500/50'
        }`}>
          {message.type === 'success' ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Story Content */}
        <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-sky-400" />
            Conținut ({LANGUAGES.find(l => l.code === selectedLang)?.name})
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Titlu Pagină
              </label>
              <input
                type="text"
                value={currentLangData.title || ''}
                onChange={(e) => updateField('title', e.target.value)}
                placeholder="Povestea Mea cu LifeWave"
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Introducere
              </label>
              <textarea
                value={currentLangData.intro || ''}
                onChange={(e) => updateField('intro', e.target.value)}
                rows={4}
                placeholder="Povestea ta despre cum ai descoperit LifeWave..."
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Conținut Principal (poți folosi paragrafe separate cu Enter)
              </label>
              <textarea
                value={currentLangData.content || ''}
                onChange={(e) => updateField('content', e.target.value)}
                rows={12}
                placeholder="Scrie aici povestea ta completă..."
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Citat/Quote (opțional)
              </label>
              <input
                type="text"
                value={currentLangData.quote || ''}
                onChange={(e) => updateField('quote', e.target.value)}
                placeholder="Un citat inspirațional..."
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50"
              />
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="p-4 bg-sky-500/10 border border-sky-500/30 rounded-xl">
          <p className="text-sky-300 text-sm">
            <strong>💡 Sfat:</strong> Completează conținutul pentru fiecare limbă selectând steagul corespunzător. 
            Dacă lași o limbă goală, se va folosi textul din română.
          </p>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={saving}
            className="bg-sky-500 hover:bg-sky-600 text-white rounded-xl px-8"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Se salvează...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                Salvează Povestea
              </span>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminMyStory;
